import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Scene, Storyboard } from '@muidsoft/shared';
import { ASPECT_RATIO } from '@muidsoft/shared';
import { api } from '../lib/api.js';
import { getSelectedProductId } from '../lib/local.js';
import { IconRefresh, IconCheckCircle, IconArrowLeft } from '../components/Icons.js';

interface StoryboardResponse extends Storyboard {
  scenes: Scene[];
}

// ---------------------------------------------------------------------------
// SceneCard — tampilkan satu scene, support inline edit saat awaiting_approval
// ---------------------------------------------------------------------------

function SceneCard({
  scene,
  canEdit,
  onUpdated,
}: {
  scene: Scene;
  canEdit: boolean;
  onUpdated: (updated: Scene) => void;
}) {
  const [editing, setEditing]               = useState(false);
  const [visualDescription, setVisualDescription] = useState(scene.visualDescription);
  const [scenePrompt, setScenePrompt]       = useState(scene.scenePrompt ?? '');
  const [voiceoverLine, setVoiceoverLine]   = useState(scene.voiceoverLine ?? '');
  const [saving, setSaving]                 = useState(false);
  const [error, setError]                   = useState<string | null>(null);

  async function handleSave() {
    if (!visualDescription.trim()) return;
    setSaving(true); setError(null);
    try {
      const body: Record<string, unknown> = { visualDescription: visualDescription.trim() };
      if (scenePrompt.trim()) body.scenePrompt = scenePrompt.trim();
      // Kirim voiceoverLine (bisa string kosong → null di backend)
      body.voiceoverLine = voiceoverLine.trim() || null;

      const res = await api.patch<{ data: Scene }>(`/api/bff/scenes/${scene.id}`, body);
      onUpdated(res.data);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setVisualDescription(scene.visualDescription);
    setScenePrompt(scene.scenePrompt ?? '');
    setVoiceoverLine(scene.voiceoverLine ?? '');
    setError(null);
    setEditing(false);
  }

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      {/* Header scene */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <strong style={{ fontSize: 13 }}>Scene {scene.orderIndex + 1}</strong>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="text-muted mono" style={{ fontSize: 12 }}>{scene.durationSeconds}s</span>
          {canEdit && !editing && (
            <button
              className="btn-ghost"
              style={{ fontSize: 11, padding: '2px 8px' }}
              onClick={() => setEditing(true)}
            >
              ✏️ Edit
            </button>
          )}
        </div>
      </div>

      {editing ? (
        /* ── Form Edit ── */
        <div>
          <div className="field-group" style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 12 }}>Deskripsi Visual *</label>
            <textarea
              rows={3}
              value={visualDescription}
              onChange={(e) => setVisualDescription(e.target.value)}
              style={{ fontSize: 13 }}
            />
          </div>
          <div className="field-group" style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 12 }}>Scene Prompt (opsional — untuk AI tools)</label>
            <textarea
              rows={2}
              value={scenePrompt}
              onChange={(e) => setScenePrompt(e.target.value)}
              placeholder="Kosong = pakai deskripsi visual di atas"
              style={{ fontSize: 12 }}
            />
          </div>
          <div className="field-group" style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 12 }}>Voiceover / Narasi</label>
            <textarea
              rows={2}
              value={voiceoverLine}
              onChange={(e) => setVoiceoverLine(e.target.value)}
              style={{ fontSize: 12 }}
            />
          </div>
          {error && (
            <p style={{ color: 'var(--status-error)', fontSize: 12, marginBottom: 8 }}>{error}</p>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-ghost" style={{ flex: 1, fontSize: 12 }} onClick={handleCancel} disabled={saving}>
              Batal
            </button>
            <button
              className="btn-primary"
              style={{ flex: 2, fontSize: 12 }}
              onClick={handleSave}
              disabled={saving || !visualDescription.trim()}
            >
              {saving ? 'Menyimpan...' : 'Simpan Scene'}
            </button>
          </div>
        </div>
      ) : (
        /* ── View Mode ── */
        <>
          <p style={{ fontSize: 14, margin: '4px 0 8px' }}>{scene.visualDescription}</p>
          {scene.scenePrompt && (
            <p className="text-muted" style={{ fontSize: 12, marginBottom: 4 }}>
              <strong>Prompt:</strong> {scene.scenePrompt}
            </p>
          )}
          {scene.voiceoverLine && (
            <p className="text-muted" style={{ fontSize: 13, fontStyle: 'italic' }}>
              &ldquo;{scene.voiceoverLine}&rdquo;
            </p>
          )}
          {scene.captionText && (
            <p className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
              <strong>Caption:</strong> {scene.captionText}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StoryboardEditSheet — edit parameter utama storyboard sebelum approve
// ---------------------------------------------------------------------------

interface EditSheetProps {
  storyboard: StoryboardResponse;
  onClose: () => void;
  onSaved: (updated: StoryboardResponse) => void;
}

function StoryboardEditSheet({ storyboard, onClose, onSaved }: EditSheetProps) {
  const [hook, setHook]               = useState(storyboard.hook);
  const [tone, setTone]               = useState(storyboard.tone);
  const [audience, setAudience]       = useState(storyboard.audience);
  const [cta, setCta]                 = useState(storyboard.cta);
  const [objective, setObjective]     = useState(storyboard.objective);
  const [aspectRatio, setAspectRatio] = useState<typeof storyboard.aspectRatio>(storyboard.aspectRatio);
  const [durationSeconds, setDurationSeconds] = useState(storyboard.durationSeconds);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState<string | null>(null);

  async function handleSave() {
    setSaving(true); setError(null);
    try {
      const res = await api.patch<{ data: StoryboardResponse }>(`/api/bff/storyboards/${storyboard.id}`, {
        hook: hook.trim() || undefined,
        tone: tone.trim() || undefined,
        audience: audience.trim() || undefined,
        cta: cta.trim() || undefined,
        objective: objective.trim() || undefined,
        aspectRatio,
        durationSeconds,
      });
      onSaved({ ...res.data, scenes: storyboard.scenes });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menyimpan perubahan');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__handle" />
        <h2 style={{ marginBottom: 16 }}>Edit Storyboard</h2>

        <div className="field-group">
          <label>Hook (kalimat pembuka)</label>
          <input value={hook} onChange={(e) => setHook(e.target.value)} />
        </div>
        <div className="field-group">
          <label>Tone</label>
          <input value={tone} onChange={(e) => setTone(e.target.value)} placeholder="mis. Santai dan meyakinkan" />
        </div>
        <div className="field-group">
          <label>Target audience</label>
          <input value={audience} onChange={(e) => setAudience(e.target.value)} />
        </div>
        <div className="field-group">
          <label>CTA (call-to-action)</label>
          <input value={cta} onChange={(e) => setCta(e.target.value)} placeholder="mis. Beli sekarang" />
        </div>
        <div className="field-group">
          <label>Objective</label>
          <textarea rows={2} value={objective} onChange={(e) => setObjective(e.target.value)} />
        </div>
        <div className="field-row">
          <div className="field-group">
            <label>Platform</label>
            <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value as typeof aspectRatio)}>
              {(ASPECT_RATIO as readonly string[]).map((r) => (
                <option key={r} value={r}>
                  {r === '9:16' ? '9:16 (TikTok/Reels)' : r === '1:1' ? '1:1 (Feed)' : '16:9 (YouTube)'}
                </option>
              ))}
            </select>
          </div>
          <div className="field-group">
            <label>Durasi (detik)</label>
            <select value={durationSeconds} onChange={(e) => setDurationSeconds(Number(e.target.value))}>
              <option value={15}>15 detik</option>
              <option value={30}>30 detik</option>
              <option value={60}>60 detik</option>
            </select>
          </div>
        </div>

        {error && <p style={{ color: 'var(--status-error)', fontSize: 13 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button className="btn-ghost" style={{ flex: 1 }} onClick={onClose} disabled={saving}>Batal</button>
          <button className="btn-primary" style={{ flex: 2 }} onClick={handleSave} disabled={saving}>
            {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StoryboardPage utama
// ---------------------------------------------------------------------------

export function StoryboardPage() {
  const { storyboardId: routeStoryboardId } = useParams();
  const navigate = useNavigate();

  const [storyboard, setStoryboard]   = useState<StoryboardResponse | null>(null);
  const [relatedJobId, setRelatedJobId] = useState<string | null>(null);
  const [loading, setLoading]         = useState(true);
  const [approving, setApproving]     = useState(false);
  const [editOpen, setEditOpen]       = useState(false);
  const [error, setError]             = useState<string | null>(null);

  const load = useCallback(async (id: string) => {
    const res = await api.get<{ data: StoryboardResponse }>(`/api/bff/storyboards/${id}`);
    const sb = res.data;
    setStoryboard(sb);

    // Cari job terkait supaya bisa link ke progress saat storyboard sudah approved
    try {
      const jobsRes = await api.get<{ data: Array<{ id: string; storyboardId?: string | null }> }>(
        `/api/bff/jobs?productId=${sb.productId}&limit=20`,
      );
      const related = jobsRes.data.find((j) => j.storyboardId === sb.id);
      setRelatedJobId(related?.id ?? null);
    } catch { /* tidak kritis — link saja tidak tampil */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (routeStoryboardId) {
          // Akses langsung via URL (/storyboard/:id) — tampilkan apa adanya
          await load(routeStoryboardId);
        } else {
          // Tab Storyboard diklik tanpa ID:
          // Hanya tampilkan storyboard yang sedang menunggu review (awaiting_approval).
          // Kalau tidak ada → biarkan null → render empty state yang informatif.
          const productId = getSelectedProductId();
          if (productId) {
            const jobsRes = await api.get<{
              data: Array<{ id: string; storyboardId?: string | null; currentStep: string }>;
            }>(`/api/bff/jobs?productId=${productId}&limit=10`);

            // Cari job yang sedang awaiting_approval dan sudah punya storyboardId
            const reviewJob = jobsRes.data.find(
              (j) => j.currentStep === 'awaiting_approval' && j.storyboardId,
            );
            if (reviewJob?.storyboardId) {
              await load(reviewJob.storyboardId);
            }
            // Tidak ada awaiting_approval → storyboard tetap null
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Gagal memuat storyboard');
      } finally {
        setLoading(false);
      }
    })();
  }, [routeStoryboardId, load]);

  async function handleApprove() {
    if (!storyboard) return;
    setApproving(true); setError(null);
    try {
      const res = await api.post<{ data: { storyboardId: string; jobId: string; enqueuedStage: string } }>(
        `/api/bff/storyboards/${storyboard.id}/approve`,
      );
      navigate(`/jobs/${res.data.jobId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal approve storyboard');
    } finally {
      setApproving(false);
    }
  }

  /** Update scene di state lokal setelah inline edit berhasil */
  function handleSceneUpdated(updatedScene: Scene) {
    setStoryboard((prev) =>
      prev
        ? { ...prev, scenes: prev.scenes.map((s) => (s.id === updatedScene.id ? updatedScene : s)) }
        : prev,
    );
  }

  // ── Render states ────────────────────────────────────────────────────────

  if (loading) return <p className="text-muted">Memuat storyboard...</p>;

  if (!storyboard) {
    return (
      <div className="empty-state">
        <h2>Tidak ada storyboard yang perlu di-review</h2>
        <p style={{ maxWidth: 280 }}>
          Storyboard akan muncul di sini saat job mencapai tahap <strong>Review Storyboard</strong>.
          Kalau sudah ada storyboard sebelumnya, cek di halaman Jobs → pilih job → klik "Review Storyboard".
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button className="btn-primary" onClick={() => navigate('/')}>
            Ke Studio
          </button>
          <button className="btn-ghost" onClick={() => navigate('/jobs')}>
            Lihat Jobs →
          </button>
        </div>
      </div>
    );
  }

  const sortedScenes = storyboard.scenes.slice().sort((a, b) => a.orderIndex - b.orderIndex);
  const canEdit      = storyboard.status === 'awaiting_approval';

  return (
    <div>
      {/* ── Tombol kembali ── */}
      <button
        className="btn-ghost tap-target"
        style={{ marginBottom: 12, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
        onClick={() => navigate(-1)}
      >
        <IconArrowLeft size={15} /> Kembali
      </button>

      {/* ── Header storyboard ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <h1 style={{ fontSize: 20, flex: 1, marginRight: 8 }}>{storyboard.hook}</h1>
        {canEdit && (
          <button className="btn-ghost tap-target" style={{ fontSize: 13, padding: '6px 12px', flexShrink: 0 }}
            onClick={() => setEditOpen(true)}>
            ✏️ Edit
          </button>
        )}
      </div>

      <div className="text-muted mono" style={{ fontSize: 12, marginBottom: 16 }}>
        {storyboard.aspectRatio} · {storyboard.durationSeconds}s · {storyboard.tone} · {storyboard.status}
      </div>

      {/* ── Metadata storyboard ── */}
      <div className="card" style={{ marginBottom: 16, fontSize: 13 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
          <div><span className="text-muted">Audience:</span> {storyboard.audience}</div>
          <div><span className="text-muted">Objective:</span> {storyboard.objective}</div>
          <div><span className="text-muted">Tone:</span> {storyboard.tone}</div>
          <div><span className="text-muted">CTA:</span> {storyboard.cta}</div>
        </div>
      </div>

      {/* ── Scene list ── */}
      <h2 style={{ fontSize: 15, marginBottom: 8 }}>
        {sortedScenes.length} Scene
        {canEdit && (
          <span className="text-muted" style={{ fontSize: 12, fontWeight: 400, marginLeft: 8 }}>
            — klik ✏️ Edit di tiap scene untuk mengubah kontennya
          </span>
        )}
      </h2>

      {sortedScenes.map((scene) => (
        <SceneCard
          key={scene.id}
          scene={scene}
          canEdit={canEdit}
          onUpdated={handleSceneUpdated}
        />
      ))}

      {error && <p style={{ color: 'var(--status-error)', fontSize: 13, marginTop: 8 }}>{error}</p>}

      {/* ── CTA approve ── */}
      {storyboard.status === 'awaiting_approval' && (
        <div className="sticky-cta">
          <button className="btn-primary" onClick={handleApprove} disabled={approving}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <IconCheckCircle size={16} />
              {approving ? 'Menyetujui...' : 'Setujui & Buat Prompts AI'}
            </span>
          </button>
        </div>
      )}

      {/* ── Status: approved (sedang diproses) ── */}
      {storyboard.status === 'approved' && (
        <div style={{
          marginTop: 16, padding: '12px 14px', borderRadius: 10,
          background: 'rgba(50,180,100,0.08)', border: '1px solid rgba(50,180,100,0.25)',
          fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--status-success, #2ecc71)' }}>
            <IconRefresh size={14} /> AI sedang membuat formatted prompts...
          </span>
          {relatedJobId && (
            <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px', flexShrink: 0 }}
              onClick={() => navigate(`/jobs/${relatedJobId}`)}>
              Lihat Progress →
            </button>
          )}
        </div>
      )}

      {/* Edit sheet */}
      {editOpen && (
        <StoryboardEditSheet
          storyboard={storyboard}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => setStoryboard(updated)}
        />
      )}
    </div>
  );
}
